/**
 * Landing page — shared by the dark ("/") and editorial ("/editorial/")
 * entries. The page's theme comes from <html data-theme>; the gallery cards
 * embed the viewer in mini mode so every preview is a live, settled render in
 * the matching theme.
 */

import "./site.css";
import { examplesByLens } from "./registry.js";
import { LENS_INFO } from "./lens.js";
import type { LensExample } from "./lens.js";
import type { ThemeName } from "./theme.js";

const theme: ThemeName =
  document.documentElement.dataset.theme === "editorial" ? "editorial" : "dark";
const otherTheme: ThemeName = theme === "dark" ? "editorial" : "dark";
const otherHref = otherTheme === "editorial" ? "/editorial/" : "/";

const app = document.getElementById("app") as HTMLElement;

const card = (e: LensExample) => `
  <a class="card" href="/view.html?ex=${e.id}&theme=${theme}">
    <iframe class="card-preview" loading="lazy"
      src="/view.html?ex=${e.id}&theme=${theme}&mini=1" title="${e.title} preview"></iframe>
    <div class="card-body">
      <div class="card-title">${e.title}</div>
      <div class="card-blurb">${e.blurb}</div>
      <div class="card-tags">${e.highlights
        .map((h) => `<span class="tag">${h}</span>`)
        .join("")}</div>
    </div>
  </a>`;

const lensSections = examplesByLens()
  .map(({ lens, examples }) => {
    const info = LENS_INFO[lens];
    return `
    <div class="lens-section">
      <div class="lens-heading">${info.label}</div>
      <div class="lens-tagline">${info.tagline}</div>
      <div class="cards-grid">${examples.map(card).join("")}</div>
    </div>`;
  })
  .join("");

app.innerHTML = `
  <header class="site-header">
    <a class="wordmark" href="${theme === "editorial" ? "/editorial/" : "/"}">viz·studio</a>
    <div class="header-links">
      <a href="/guide/">Guide</a>
      <a href="${otherHref}">${otherTheme} theme</a>
      <a href="https://github.com/jonnydubowsky/viz-playground" target="_blank" rel="noopener">GitHub</a>
    </div>
  </header>

  <main>
    <section class="landing-inner">
      <div class="landing-eyebrow">Visualization Playground · Six lenses</div>
      <h1 class="landing-title">A canvas of lenses for graph intelligence.</h1>
      <p class="landing-text">
        <strong>Viz Studio</strong> is a multi-lens visualization suite for Claude
        agents and client deployments. When a workflow surfaces complex data
        mid-session, any agent can render multi-dimensional insight &mdash; force
        dynamics, value flows, temporal streams, coverage, hierarchy, capability
        profiles &mdash; with enough visual encoding space to carry rich, layered
        information.
      </p>
      <p class="landing-text">
        Six lenses read the same world six ways: <strong>force-directed</strong>
        networks, <strong>Sankey</strong> flows, <strong>timeline</strong>
        swimlanes, <strong>matrix</strong> heatmaps, <strong>tree</strong>
        hierarchies, and <strong>radar</strong> profiles. All share one design
        language reverse-engineered from the Hooktheory Trends chord graph &mdash;
        a narrow <strong>constellation</strong> band, sub-linear sizing, a
        dynamic palette &mdash; and every example renders in two skins: the dark
        constellation and a light <strong>editorial</strong> variant. Open any
        one, switch the theme, export the SVG.
      </p>
    </section>

    <section class="cards-section">
      <div class="cards-label">${theme} theme</div>
      ${lensSections}
    </section>
  </main>`;
